import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  ListChecks, 
  BookOpen, 
  ChartBar,
  CheckCircle,
  XCircle,
  Upload,
  Eye
} from '@phosphor-icons/react';
import { dashboardApi } from '../lib/api';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await dashboardApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      label: 'Total Students',
      value: stats?.totalStudents || 0,
      icon: Users,
      color: '#E31E25',
      link: '/admin/students',
    },
    {
      label: 'Total Results',
      value: stats?.totalResults || 0,
      icon: ListChecks,
      color: '#666666',
      link: '/admin/results',
    },
    {
      label: 'Published Results',
      value: stats?.publishedResults || 0,
      icon: Eye,
      color: '#00A859',
      link: '/admin/results',
    },
    {
      label: 'Programs',
      value: stats?.totalPrograms || 0,
      icon: BookOpen,
      color: '#FFB300',
      link: '/admin/programs',
    },
  ];

  const quickActions = [
    { label: 'Upload Students', icon: Upload, path: '/admin/upload-students', color: '#E31E25' },
    { label: 'Upload Results', icon: Upload, path: '/admin/upload-results', color: '#E31E25' },
    { label: 'View Students', icon: Users, path: '/admin/students', color: '#666666' },
    { label: 'View Results', icon: ListChecks, path: '/admin/results', color: '#666666' },
  ];

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-[#E8E8E8] p-4 sm:p-6 animate-pulse">
              <div className="h-3 sm:h-4 bg-[#E8E8E8] w-16 sm:w-24 mb-3 sm:mb-4" />
              <div className="h-6 sm:h-8 bg-[#E8E8E8] w-12 sm:w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <p className="overline mb-1 sm:mb-2">Overview</p>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Dashboard
        </h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-grid">
        {statCards.map((stat, idx) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white border border-[#E8E8E8] p-4 sm:p-6 hover:border-[#E31E25] transition-colors group animate-fade-in-up"
            style={{ animationDelay: `${idx * 100}ms` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-[#666666] mb-1 truncate">{stat.label}</p>
                <p className="text-2xl sm:text-3xl font-black text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {stat.value}
                </p>
              </div>
              <stat.icon size={24} className="sm:w-8 sm:h-8 flex-shrink-0" weight="duotone" style={{ color: stat.color }} />
            </div>
          </Link>
        ))}
      </div>

      {/* Pass/Fail Stats */}
      {stats && (stats.passCount > 0 || stats.failCount > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-white border border-[#E8E8E8] p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <CheckCircle size={20} className="sm:w-6 sm:h-6" weight="duotone" className="text-[#00A859]" />
              <span className="font-semibold text-sm sm:text-base text-[#1A1A1A]">Pass Statistics</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-3xl sm:text-4xl font-black text-[#00A859]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {stats.passCount}
              </span>
              <span className="text-[#666666] mb-1 text-sm">results</span>
            </div>
            {stats.totalResults > 0 && (
              <div className="mt-3 sm:mt-4 h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#00A859] transition-all duration-500" 
                  style={{ width: `${(stats.passCount / stats.totalResults) * 100}%` }}
                />
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E8E8E8] p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <XCircle size={20} className="sm:w-6 sm:h-6" weight="duotone" className="text-[#E31E25]" />
              <span className="font-semibold text-sm sm:text-base text-[#1A1A1A]">Fail Statistics</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-3xl sm:text-4xl font-black text-[#E31E25]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {stats.failCount}
              </span>
              <span className="text-[#666666] mb-1 text-sm">results</span>
            </div>
            {stats.totalResults > 0 && (
              <div className="mt-3 sm:mt-4 h-2 bg-[#E8E8E8] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#E31E25] transition-all duration-500" 
                  style={{ width: `${(stats.failCount / stats.totalResults) * 100}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-3 sm:mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {quickActions.map((action, idx) => (
            <Link
              key={action.label}
              to={action.path}
              className="bg-white border border-[#E8E8E8] p-3 sm:p-4 hover:border-[#E31E25] transition-colors flex flex-col items-center gap-2 text-center animate-fade-in-up"
              style={{ animationDelay: `${(idx + 4) * 100}ms` }}
              data-testid={`action-${action.label.toLowerCase().replace(' ', '-')}`}
            >
              <action.icon size={24} className="sm:w-7 sm:h-7" weight="duotone" style={{ color: action.color }} />
              <span className="text-xs sm:text-sm font-medium text-[#1A1A1A]">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
